import React, { useState, useRef, useEffect } from 'react';
import { Box, Heading, Flex, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { FaEdit, FaBell, FaCog } from 'react-icons/fa';
import { useUserStore } from '../store/userStore';
import { useModernToast } from '../components/ModernToast';
import ProfileTab from '../components/ProfileTab';
import NotificationsTab from '../components/NotificationsTab';
import SettingsTab from '../components/SettingsTab';
// import BillingInfoTab from '../components/BillingInfoTab';
// import PaymentHistoryTab from '../components/PaymentHistoryTab';

const MyProfilePage = () => {
  const { user, updateUser } = useUserStore();
  const [editing, setEditing] = useState<{ [key: string]: boolean }>({});
  const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState<{ [key: string]: boolean }>({});
  const [showName, setShowName] = useState(user?.show_name || false);
  const [username, setUsername] = useState(user?.username === user?.email ? '' : user?.username || '');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const toast = useModernToast();

  // Refs for each input
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  // Focus input and move cursor to end when editing a field
  useEffect(() => {
    Object.keys(editing).forEach(key => {
      if (editing[key] && inputRefs.current[key]) {
        const input = inputRefs.current[key];
        input?.focus();
        // Only set selection range for supported types
        if (
          input &&
          ['text', 'search', 'password', 'tel', 'url'].includes(input.type) &&
          input.value
        ) {
          const len = input.value.length;
          input.setSelectionRange(len, len);
        }
      }
    });
  }, [editing]);

  const handleEdit = (key: string) => {
    // Set only the selected field to true, others to false
    const newEditing: { [key: string]: boolean } = {};
    // Define fields here since we moved them to ProfileTab
    const fields = ['username', 'email', 'first_name', 'last_name', 'phone', 'instagram', 'x', 'facebook'];
    fields.forEach(f => { newEditing[f] = false; });
    newEditing[key] = true;
    setEditing(newEditing);
    // Only keep edit value for the current field
    setEditValues({ [key]: String(user?.[key as keyof typeof user] ?? '') });
  };

  const handleCancel = (key: string) => {
    setEditing({ ...editing, [key]: false });
    setEditValues({ ...editValues, [key]: '' });
  };

  const handleChange = (key: string, value: string) => {
    setEditValues({ ...editValues, [key]: String(value) });
  };

  const handleSave = async (key: string) => {
    if (!user) return;
    try {
      setIsSaving({ ...isSaving, [key]: true });
      await updateUser({
        [key]: editValues[key],
        show_name: Boolean(showName),
        username: String(username || ''),
      });
      setEditing({ ...editing, [key]: false });
      toast.success('Success', 'Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Error', 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving({ ...isSaving, [key]: false });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Box p={8} color="white">
      <Flex align="center" mb={8} gap={4}>
        <Heading
          as="h1"
          fontFamily="Audiowide"
          fontWeight="bold"
          color="white"
          flex="1"
          textAlign="left"
          fontSize="2xl"
          m={0}
          p={0}
          whiteSpace="nowrap"
        >
          My Profile
        </Heading>
      </Flex>
      
      <Tabs colorScheme="blue" variant="enclosed">
        <TabList>
          <Tab>
            <FaEdit style={{ marginRight: '8px' }} />
            Profile
          </Tab>
          <Tab>
            <FaBell style={{ marginRight: '8px' }} />
            Notifications
          </Tab>
          <Tab>
            <FaCog style={{ marginRight: '8px' }} />
            Settings
          </Tab>
          {/* <Tab>
            <FaCreditCard style={{ marginRight: '8px' }} />
            Billing Info
          </Tab>
          <Tab>
            <FaHistory style={{ marginRight: '8px' }} />
            Payment History
          </Tab> */}
        </TabList>

        <TabPanels>
          {/* Profile Tab */}
          <TabPanel>
            <ProfileTab 
              user={user}
              editing={editing}
              setEditing={setEditing}
              editValues={editValues}
              setEditValues={setEditValues}
              isSaving={isSaving}
              setIsSaving={setIsSaving}
              showName={showName}
              setShowName={setShowName}
              username={username}
              setUsername={setUsername}
              usernameTouched={usernameTouched}
              setUsernameTouched={setUsernameTouched}
              handleEdit={handleEdit}
              handleCancel={handleCancel}
              handleChange={handleChange}
              handleSave={handleSave}
            />
          </TabPanel>

          {/* Notifications Tab */}
          <TabPanel>
            <NotificationsTab />
          </TabPanel>

          {/* Settings Tab */}
          <TabPanel>
            <SettingsTab />
          </TabPanel>

          {/* Billing Info Tab */}
          {/* <TabPanel>
            <BillingInfoTab />
          </TabPanel> */}

          {/* Payment History Tab */}
          {/* <TabPanel>
            <PaymentHistoryTab />
          </TabPanel> */}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default MyProfilePage; 